/**
 * Напоминания о привычках.
 *
 * Время напоминания задавалось в форме привычки, сохранялось и показывалось в
 * карточке — но уведомление никогда не заводилось. Этот модуль закрывает
 * разрыв: он превращает `reminderMin` в запись системы уведомлений.
 *
 * Как и напоминания о лекарствах, модуль тонкий: он не запрашивает разрешение
 * и ничего не показывает сам. Если уведомления выключены, вызовы просто
 * ничего не делают, и трекер продолжает работать как журнал.
 */

import { isScheduledOn } from '@/entities/habit/model/habit-score';
import type { Habit, HabitEntry } from '@/shared/db/schema';
import type { IsoDate } from '@/shared/lib/datetime';
import { cancel, schedule } from '@/shared/notifications/local-notifications';

/** Идентификатор привязан к привычке и дню: повторный вызов заменяет запись. */
function reminderId(habit: Habit, date: IsoDate): string {
  return `habit-${habit.id}-${date}`;
}

/**
 * Приводит напоминания в соответствие с состоянием привычек на сегодня.
 *
 * Напоминание снимается, если привычка уже отмечена: подталкивать к тому, что
 * человек сделал полчаса назад, — верный способ научить его игнорировать
 * уведомления вообще.
 */
export function syncHabitReminders(
  habits: Habit[],
  entries: HabitEntry[],
  today: IsoDate,
): void {
  const markedToday = new Set(
    entries
      .filter((entry) => entry.date === today && entry.deletedAt === null && entry.value > 0)
      .map((entry) => entry.habitId),
  );

  for (const habit of habits) {
    const id = reminderId(habit, today);

    const skip =
      habit.deletedAt !== null ||
      habit.archivedAt !== null ||
      habit.reminderMin === null ||
      habit.startDate > today ||
      // В день, когда привычка не запланирована, напоминать не о чем.
      !isScheduledOn(habit, today) ||
      markedToday.has(habit.id);

    if (skip) {
      void cancel(id);
      continue;
    }

    void schedule({
      id,
      title: habit.title,
      // Вопрос формулировали при создании привычки — он звучит человечнее,
      // чем сухое «пора выполнить».
      body: habit.question.trim() || 'Пора отметить привычку',
      category: 'habit',
      date: today,
      timeMin: habit.reminderMin!,
      route: '/goals',
      // По этой ссылке кнопка «Выполнено» закрывает день привычки, не
      // открывая экран.
      target: { kind: 'habit', id: habit.id, date: today },
    });
  }
}

/** Снимает все напоминания привычек — например, при выходе из аккаунта. */
export function cancelHabitReminders(habits: Habit[], date: IsoDate): void {
  for (const habit of habits) void cancel(reminderId(habit, date));
}
